// Defaults y utilidades de filtros compartidos entre la Lista y el Kanban de
// validación de servicios.
//
// Rango de fecha requerido con presets (por defecto Hoy). El backend filtra por
// Booking.scheduled_start con date_from/date_to (YYYY-MM-DD).

export const DATE_RANGE_DEFAULT = "today"

// Presets disponibles (id + etiqueta i18n key). "custom" habilita dos fechas.
export const DATE_RANGE_PRESETS = [
  { id: "today", labelKey: "Today", labelFallback: "Hoy" },
  { id: "yesterday", labelKey: "Yesterday", labelFallback: "Ayer" },
  { id: "last7", labelKey: "Last7Days", labelFallback: "Últimos 7 días" },
  { id: "last15", labelKey: "Last15Days", labelFallback: "Últimos 15 días" },
  { id: "last30", labelKey: "Last30Days", labelFallback: "Últimos 30 días" },
  { id: "custom", labelKey: "CustomRange", labelFallback: "Rango personalizado" },
]

export const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`

// Filtros COMPARTIDOS entre Lista y Kanban (se sincronizan vía URL para que lo
// filtrado en una vista se refleje en la otra). ``status`` NO se comparte: es
// exclusivo de la lista (el kanban agrupa por estatus en columnas); se preserva
// en la URL pero cada vista decide si lo usa.
export const SHARED_FILTER_KEYS = [
  "company_id", "code", "plate", "place_id",
  "validated", "route_health", "date_range",
]

// Hidrata un objeto de filtros base a partir de los query params de la URL,
// sobrescribiendo solo las claves presentes.
//
// Rango de fecha: se respetan date_from/date_to EXPLICITOS de la URL para
// cualquier date_range (preset o "custom"). Solo cuando el preset no trae
// fechas explicitas se recalcula la ventana desde ``date_range`` con la fecha
// de hoy. Esto evita pisar las fechas que vienen en la URL (bug PT-4070).
// ``searchParams`` es un URLSearchParams. Devuelve una copia nueva.
export const hydrateFiltersFromParams = (base, searchParams) => {
  const f = { ...base }
  Object.keys(f).forEach((k) => {
    const v = searchParams.get(k)
    if (v != null && v !== "") f[k] = v
  })
  if (f.date_range) {
    const df = searchParams.get("date_from")
    const dt = searchParams.get("date_to")
    if (f.date_range === "custom") {
      if (df) f.date_from = df
      if (dt) f.date_to = dt
    } else if (df && dt) {
      // Preset con fechas explicitas en la URL: respetarlas, no recalcular.
      f.date_from = df
      f.date_to = dt
    } else {
      const r = dateRangeValue(f.date_range)
      if (r) {
        f.date_from = r.date_from
        f.date_to = r.date_to
      }
    }
  }
  return f
}

// Indica si unos query params representan "un retorno con filtros a respetar"
// (es decir, venimos de una URL de retorno y NO de una entrada "en frio").
// Clave para el bug PT-4074: si hay date_range + date_from/date_to explicitos,
// esos mandan y el montaje NO debe reescribir la URL con el default 'today'.
// ``searchParams`` es un URLSearchParams (o null). Funcion pura y testeable.
export const hasMeaningfulFilterParams = (searchParams) => {
  if (!searchParams) return false
  const keys = ["date_range", "date_from", "date_to", "status", "validated", "sort_by", "sort_dir", "page"]
  return keys.some((k) => {
    const v = searchParams.get(k)
    return v != null && v !== ""
  })
}

// ---------------------------------------------------------------------------
// FUENTE ÚNICA DE VERDAD (PT-4075): los filtros viven en la URL. El estado
// aplicado DERIVA de searchParams en cada render (no se "hidrata una sola vez").
// El default 'today' SOLO se usa cuando la URL no trae filtros significativos;
// si la URL trae filtros, mandan siempre. Así List y Kanban comparten el mismo
// mecanismo y no hay un estado local paralelo que pueda caer al default y pisar
// la URL de retorno (causa raíz del bug de los 3 intentos previos).
// ---------------------------------------------------------------------------

// Resuelve los filtros APLICADOS a partir de los query params de la URL.
// ``makeDefaults`` es la fábrica de filtros base de la vista (List/Kanban
// tienen shapes distintos: List incluye ``status``). Si la URL no trae filtros
// significativos (entrada "en frío"), devuelve los defaults (rango 'today'). Si
// los trae (retorno desde detalle o toggle list<->kanban), hidrata respetando
// las fechas explícitas de la URL. Función pura y determinista.
export const resolveFiltersFromUrl = (searchParams, makeDefaults) => {
  const base = makeDefaults()
  if (!hasMeaningfulFilterParams(searchParams)) return base
  return hydrateFiltersFromParams(base, searchParams)
}

// Serializa los filtros aplicados + orden + página a un objeto plano apto para
// ``setSearchParams``. Omite vacíos; ``date_range`` viaja como meta del selector
// y ``date_from/date_to`` viajan SIEMPRE que haya rango resuelto (así la fecha
// sobrevive el round-trip lista<->detalle<->kanban sin recalcular presets).
// ``page`` solo se incluye si es > 1. Función pura.
export const filtersToSearchParams = (filters = {}, { sortBy, sortDir, page } = {}) => {
  const q = {}
  Object.entries(filters).forEach(([k, v]) => {
    if (v === "" || v == null) return
    q[k] = String(v)
  })
  if (sortBy) q.sort_by = sortBy
  if (sortDir) q.sort_dir = sortDir
  if (page != null && Number(page) > 1) q.page = String(page)
  return q
}

// Devuelve { date_from, date_to } (YYYY-MM-DD) para el preset dado.
// - today: hoy .. hoy
// - yesterday: ayer .. ayer
// - last7 / last15 / last30: (hoy - N + 1) .. hoy  (ventana que incluye hoy)
// - custom: no calcula (el caller mantiene las fechas elegidas por el usuario)
export const dateRangeValue = (range) => {
  const now = new Date()
  const daysAgo = (n) => {
    const d = new Date(now)
    d.setDate(now.getDate() - n)
    return d
  }
  switch (range) {
    case "yesterday": {
      const y = daysAgo(1)
      return { date_from: iso(y), date_to: iso(y) }
    }
    case "last7":
      return { date_from: iso(daysAgo(6)), date_to: iso(now) }
    case "last15":
      return { date_from: iso(daysAgo(14)), date_to: iso(now) }
    case "last30":
      return { date_from: iso(daysAgo(29)), date_to: iso(now) }
    case "custom":
      // El caller conserva date_from/date_to actuales.
      return null
    case "today":
    default:
      return { date_from: iso(now), date_to: iso(now) }
  }
}
